import React, { useEffect, useRef } from 'react';
import {
  CandlestickSeries,
  ColorType,
  createChart,
  HistogramSeries,
} from 'lightweight-charts';

const BAR_SECONDS = 60;
const SEED_BARS = 320;
const TICK_MS = 120;
const FINALIZE_AFTER_TICKS = 16;
/** Initial zoom: how many closed bars are in view (fewer = more zoomed in). */
const VISIBLE_BARS = 55;
/** Empty bar-slots to the right of the latest candle (away from the price axis). */
const RIGHT_OFFSET = 18;
const BAR_SPACING = 12;
/** Typical closed-bar volume band — keeps histogram heights consistent. */
const VOL_MIN = 70;
const VOL_MAX = 360;
/** Per-tick volume delta while the forming candle prints (monotonic; wide variance). */
const VOL_TICK_MIN = 2;
const VOL_TICK_MAX = 48;

function randRange(min, max) {
  return min + Math.random() * (max - min);
}

function closedBarVolume(moveSize) {
  const base = randRange(VOL_MIN, VOL_MAX * 0.85);
  const moveBoost = Math.min(moveSize * 12, VOL_MAX * 0.2);
  return Math.round(Math.min(VOL_MAX, base + moveBoost));
}

function tickVolumeDelta() {
  // Mostly modest adds, sometimes medium, occasionally a big chunk.
  const u = Math.random();
  if (u < 0.55) return Math.round(randRange(VOL_TICK_MIN, 12));
  if (u < 0.85) return Math.round(randRange(12, 28));
  return Math.round(randRange(28, VOL_TICK_MAX));
}

function buildSeedBars(count) {
  const now = Math.floor(Date.now() / 1000);
  const start = now - count * BAR_SECONDS;
  const bars = [];
  let price = 100 + randRange(-5, 5);
  for (let i = 0; i < count; i++) {
    const open = price;
    const close = Math.max(1, open + randRange(-1.8, 1.8));
    const high = Math.max(open, close) + randRange(0, 0.9);
    const low = Math.min(open, close) - randRange(0, 0.9);
    const volume = closedBarVolume(Math.abs(close - open));
    bars.push({
      time: start + i * BAR_SECONDS,
      open,
      high,
      low,
      close,
      volume,
    });
    price = close;
  }
  return bars;
}

function toCandle(bar) {
  return {
    time: bar.time,
    open: bar.open,
    high: bar.high,
    low: bar.low,
    close: bar.close,
  };
}

function toVolume(bar, upColor, downColor) {
  return {
    time: bar.time,
    value: bar.volume,
    color: bar.close >= bar.open ? upColor : downColor,
  };
}

/**
 * Interactive mock chart (Lightweight Charts) shown when no broker is linked.
 * Same pan/scale gestures as the live chart; data is synthetic and keeps printing.
 */
export default function MockCandlestickBackdrop({
  upColor = '#22c55e',
  downColor = '#ef4444',
  backgroundColor = '#0C0F19',
  gridColor = 'rgba(197,203,206,0.10)',
  textColor = 'rgba(148, 163, 184, 0.85)',
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: backgroundColor || 'transparent' },
        textColor,
        fontSize: 11,
      },
      grid: {
        vertLines: { color: gridColor },
        horzLines: { color: gridColor },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.08, bottom: 0.22 },
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: RIGHT_OFFSET,
        barSpacing: BAR_SPACING,
        minBarSpacing: 4,
      },
      crosshair: {
        mode: 1,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: { time: true, price: true },
        axisDoubleClickReset: { time: true, price: true },
        mouseWheel: true,
        pinch: true,
      },
      width: container.clientWidth,
      height: container.clientHeight,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor,
      downColor,
      borderUpColor: upColor,
      borderDownColor: downColor,
      wickUpColor: upColor,
      wickDownColor: downColor,
      borderVisible: true,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });

    const seed = buildSeedBars(SEED_BARS);
    candleSeries.setData(seed.map(toCandle));
    volumeSeries.setData(seed.map((bar) => toVolume(bar, upColor, downColor)));
    // Zoom into recent bars with clear padding before the right price axis.
    chart.timeScale().setVisibleLogicalRange({
      from: seed.length - VISIBLE_BARS,
      to: seed.length - 1 + RIGHT_OFFSET,
    });

    const state = {
      lastBar: { ...seed[seed.length - 1] },
      tickCount: 0,
      disposed: false,
    };
    // Forming bar starts near empty and accumulates volume each tick.
    state.lastBar.volume = tickVolumeDelta();

    const intervalId = setInterval(() => {
      if (state.disposed) return;
      const cur = state.lastBar;
      cur.close = Math.max(1, cur.close + randRange(-1.2, 1.2));
      cur.high = Math.max(cur.high, cur.close);
      cur.low = Math.min(cur.low, cur.close);
      // Volume only grows while the candle is open (never resets mid-bar).
      cur.volume = Math.min(VOL_MAX, cur.volume + tickVolumeDelta());
      state.tickCount += 1;

      try {
        candleSeries.update(toCandle(cur));
        volumeSeries.update(toVolume(cur, upColor, downColor));
      } catch {
        /* chart may be mid-dispose */
        return;
      }

      if (state.tickCount >= FINALIZE_AFTER_TICKS) {
        const next = {
          time: cur.time + BAR_SECONDS,
          open: cur.close,
          high: cur.close,
          low: cur.close,
          close: cur.close,
          volume: tickVolumeDelta(),
        };
        state.lastBar = next;
        state.tickCount = 0;
        try {
          candleSeries.update(toCandle(next));
          volumeSeries.update(toVolume(next, upColor, downColor));
        } catch {
          /* ignore */
        }
      }
    }, TICK_MS);

    const ro = new ResizeObserver(() => {
      if (state.disposed) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w > 0 && h > 0) {
        chart.applyOptions({ width: w, height: h });
      }
    });
    ro.observe(container);

    return () => {
      state.disposed = true;
      clearInterval(intervalId);
      ro.disconnect();
      chart.remove();
    };
  }, [upColor, downColor, backgroundColor, gridColor, textColor]);

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label="Demo chart — pan and zoom while you connect a broker"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        touchAction: 'none',
      }}
    />
  );
}
